import type { ReactElement } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { UserType } from '@/types/database.types'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
  children: ReactElement
  allowedUserTypes?: UserType[]
}

export function ProtectedRoute({
  children,
  allowedUserTypes,
}: ProtectedRouteProps): ReactElement {
  const { user, profile, loading, initialized } = useAuth()
  const location = useLocation()

  // Show loading state while checking auth
  if (!initialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  // Check user type if specified
  if (allowedUserTypes && profile) {
    if (!allowedUserTypes.includes(profile.user_type)) {
      // Redirect to appropriate dashboard based on user type
      const dashboardPaths: Record<UserType, string> = {
        school: '/school/dashboard',
        recruiter: '/recruiter/dashboard',
        job_seeker: '/jobseeker/dashboard',
        admin: '/admin/dashboard',
      }
      return <Navigate to={dashboardPaths[profile.user_type]} replace />
    }
  }

  return children
}
